import React from "react";

import { i18n } from "../../../translate/i18n";

function IconChevronLeft({ className = "size-4" }) {
	return (
		<svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
			<path d="M15 18l-6-6 6-6" />
		</svg>
	);
}

function IconChevronRight({ className = "size-4" }) {
	return (
		<svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
			<path d="M9 18l6-6-6-6" />
		</svg>
	);
}

function formatDate(value) {
	if (!value) return "—";
	return new Date(value).toLocaleString("pt-BR");
}

function formatCurrency(value) {
	return new Intl.NumberFormat("pt-BR", {
		style: "currency",
		currency: "BRL",
	}).format(Number(value) || 0);
}

function getStatusMeta(status) {
	const normalized = String(status || "").toUpperCase();
	if (["RECEIVED", "CONFIRMED", "RECEIVED_IN_CASH"].includes(normalized)) {
		return {
			label: i18n.t("transactions.status.received"),
			className: "bg-success/10 text-success",
		};
	}
	if (normalized === "PENDING" || normalized === "OVERDUE") {
		return {
			label: i18n.t("transactions.status.pending"),
			className: "bg-warning/10 text-warning",
		};
	}
	return {
		label: status || "—",
		className: "bg-default-100 text-default-600 dark:bg-default-800 dark:text-default-300",
	};
}

function StatusBadge({ status }) {
	const meta = getStatusMeta(status);
	return (
		<span className={`inline-flex rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${meta.className}`}>
			{meta.label}
		</span>
	);
}

function buildPageNumbers(pageNumber, totalPages) {
	if (totalPages <= 1) return [1];

	let from = Math.max(1, pageNumber - 2);
	let to = Math.min(totalPages, from + 4);

	if (to - from < 4) {
		from = Math.max(1, to - 4);
	}

	return Array.from({ length: to - from + 1 }, (_, index) => from + index);
}

export default function TransactionsTable({
	transactions,
	loading,
	count,
	pageNumber,
	pageSize,
	totalPages,
	hasMore,
	onPageChange,
	onSelect,
}) {
	const rangeStart = count ? (pageNumber - 1) * pageSize + 1 : 0;
	const rangeEnd = Math.min(pageNumber * pageSize, count);
	const pageNumbers = buildPageNumbers(pageNumber, totalPages);

	if (loading && !transactions.length) {
		return (
			<div className="card">
				<div className="card-body py-16 text-center">
					<div className="app-modal-spinner mx-auto" />
					<p className="mt-3 text-sm text-default-500">{i18n.t("transactions.loading")}</p>
				</div>
			</div>
		);
	}

	if (!transactions.length) {
		return (
			<div className="card">
				<div className="card-body flex flex-col items-center justify-center gap-2 py-16 text-center">
					<p className="m-0 font-semibold text-default-800 dark:text-default-100">
						{i18n.t("transactions.empty")}
					</p>
				</div>
			</div>
		);
	}

	return (
		<div className="card">
			<div className={`card-body p-0 ${loading ? "opacity-60" : ""}`}>
				<div className="app-table-scroll">
					<table className="w-full min-w-[760px] table-auto text-left text-sm">
						<thead>
							<tr className="bg-default-150">
								<th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-default-500">
									{i18n.t("transactions.columns.date")}
								</th>
								<th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-default-500">
									{i18n.t("transactions.columns.contact")}
								</th>
								<th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-default-500">
									{i18n.t("transactions.columns.amount")}
								</th>
								<th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-default-500">
									{i18n.t("transactions.columns.status")}
								</th>
								<th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-default-500">
									{i18n.t("transactions.columns.dueDate")}
								</th>
								<th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-default-500">
									{i18n.t("transactions.columns.paidAt")}
								</th>
								<th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-default-500">
									{i18n.t("transactions.columns.paymentId")}
								</th>
							</tr>
						</thead>
						<tbody>
							{transactions.map((item) => (
								<tr
									key={item.id}
									className="cursor-pointer border-b border-default-200 transition-colors hover:bg-default-50 dark:border-default-700 dark:hover:bg-default-800/40"
									onClick={() => onSelect(item)}
								>
									<td className="whitespace-nowrap px-4 py-3 text-default-700 dark:text-default-200">
										{formatDate(item.createdAt)}
									</td>
									<td className="px-4 py-3">
										<div className="font-medium text-default-800 dark:text-default-100">
											{item.contact?.name || "—"}
										</div>
										<div className="text-xs text-default-500">
											{item.contact?.number || "—"}
										</div>
									</td>
									<td className="px-4 py-3 font-semibold text-default-800 dark:text-default-100">
										{formatCurrency(item.valor)}
									</td>
									<td className="px-4 py-3">
										<StatusBadge status={item.status} />
									</td>
									<td className="whitespace-nowrap px-4 py-3 text-default-600 dark:text-default-300">
										{formatDate(item.dueDate)}
									</td>
									<td className="whitespace-nowrap px-4 py-3 text-default-600 dark:text-default-300">
										{formatDate(item.paidAt)}
									</td>
									<td className="max-w-[180px] truncate px-4 py-3 font-mono text-xs text-default-500">
										{item.paymentId}
									</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			</div>

			{count > 0 && (
				<div className="card-footer flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
					<p className="m-0 text-sm text-default-500 dark:text-default-400">
						{i18n.t("transactions.pagination.summary", {
							from: rangeStart,
							to: rangeEnd,
							total: count,
						})}
					</p>
					{totalPages > 1 && (
						<nav
							className="flex flex-wrap items-center gap-2"
							aria-label={i18n.t("transactions.pagination.aria")}
						>
							<button
								type="button"
								className="btn btn-sm shrink-0 border border-default-200 bg-transparent text-default-600 hover:border-primary/10 hover:bg-primary/10 hover:text-primary disabled:cursor-not-allowed disabled:opacity-60 dark:border-default-600 dark:text-default-300"
								disabled={pageNumber <= 1 || loading}
								onClick={() => onPageChange(Math.max(1, pageNumber - 1))}
							>
								<IconChevronLeft className="size-4 me-1 shrink-0" />
								{i18n.t("transactions.pagination.prev")}
							</button>

							{pageNumbers.map((page) => (
								<button
									key={page}
									type="button"
									className={`btn size-7.5 shrink-0 border ${
										page === pageNumber
											? "border-primary bg-primary text-white"
											: "border-default-200 bg-transparent text-default-600 hover:border-primary/10 hover:bg-primary/10 hover:text-primary dark:border-default-600 dark:text-default-300"
									}`}
									disabled={loading}
									onClick={() => onPageChange(page)}
								>
									{page}
								</button>
							))}

							<button
								type="button"
								className="btn btn-sm shrink-0 border border-default-200 bg-transparent text-default-600 hover:border-primary/10 hover:bg-primary/10 hover:text-primary disabled:cursor-not-allowed disabled:opacity-60 dark:border-default-600 dark:text-default-300"
								disabled={pageNumber >= totalPages || loading || !hasMore}
								onClick={() => onPageChange(Math.min(totalPages, pageNumber + 1))}
							>
								{i18n.t("transactions.pagination.next")}
								<IconChevronRight className="size-4 ms-1 shrink-0" />
							</button>
						</nav>
					)}
				</div>
			)}
		</div>
	);
}
